import {useEffect, useRef, useState} from 'preact/hooks';
import {fetchDisclaimerCatalog, fetchDisclaimersByHandles, saveDisclaimer} from '../../../../shared/models/disclaimers';
import {DISCLAIMER_TYPE} from '../../../../shared/disclaimers.js';
import {MAX_DISCLAIMER_HANDLES as MAX_LEGACY_DISCLAIMERS} from '../../../../shared/metaobject-handles.js';
import {SortControls, SortModeButton} from './SortableTable.jsx';

export default function DisclaimersCard({ids, required, disabled, onChange, onRequiredChange, onBusyChange}) {
  const [disclaimers, setDisclaimers] = useState({});
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedDisclaimerId, setSelectedDisclaimerId] = useState('');
  const modal = useRef(null);
  const addModal = useRef(null);
  const idsKey = JSON.stringify(ids);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    fetchDisclaimersByHandles(ids).then((items) => {
      if (active) setDisclaimers(Object.fromEntries(items.map((item) => [item.handle, item])));
    }).catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Unable to load disclaimers.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [idsKey]);
  const openEditor = (disclaimer = null) => {
    setDialogError('');
    setEditing({original: disclaimer, name: disclaimer?.name || '', text: disclaimer?.text || '', handle: disclaimer?.handle || `disclaimer-${crypto.randomUUID()}`});
    modal.current?.showOverlay();
  };
  const openAdd = async () => {
    setDialogError(''); setCatalogLoading(true); setSelectedDisclaimerId('');
    addModal.current?.showOverlay();
    try {
      const items = (await fetchDisclaimerCatalog()).filter((disclaimer) => !ids.includes(disclaimer.handle));
      setCatalog(items); setSelectedDisclaimerId(items[0]?.id || '');
    } catch (e) { setDialogError(e instanceof Error ? e.message : 'Unable to load disclaimers.'); }
    finally { setCatalogLoading(false); }
  };
  const addExisting = () => {
    const disclaimer = catalog.find(({id}) => id === selectedDisclaimerId);
    if (!disclaimer) return;
    setDisclaimers((current) => ({...current, [disclaimer.handle]: disclaimer}));
    onChange([...ids, disclaimer.handle]);
    addModal.current?.hideOverlay();
  };
  const save = async () => {
    if (saving) return;
    setSaving(true); onBusyChange(true); setDialogError('');
    try {
      const saved = await saveDisclaimer(editing, editing.original);
      setDisclaimers((current) => ({...current, [saved.handle]: saved}));
      if (!ids.includes(saved.handle)) onChange([...ids, saved.handle]);
      modal.current?.hideOverlay();
    } catch (e) { setDialogError(e instanceof Error ? e.message : 'Unable to save disclaimer.'); }
    finally { setSaving(false); onBusyChange(false); }
  };
  const move = (from, to) => {
    const next = [...ids];
    const [id] = next.splice(from, 1);
    next.splice(to, 0, id);
    onChange(next);
  };
  const adminHref = editing?.original?.id
    ? `shopify:admin/content/metaobjects/${DISCLAIMER_TYPE}/${editing.original.id.split('/').pop()}`
    : '';
  return <s-section>
    <s-stack gap="small">
      <s-grid gridTemplateColumns="minmax(0, 1fr) auto" gap="small" alignItems="center">
        <s-heading>Terms &amp; restrictions</s-heading>
        <s-button variant="tertiary" icon="settings" accessibilityLabel="Terms & restrictions settings" commandFor="terms-settings-modal" command="--show" disabled={disabled} />
      </s-grid>
      {error && <s-banner tone="critical">{error}</s-banner>}
      {loading ? <s-spinner accessibilityLabel="Loading disclaimers" /> : <s-table>
        <s-table-header-row><s-table-header listSlot="primary">Disclaimer</s-table-header><s-table-header listSlot="inline" format="numeric"><s-stack direction="inline" justifyContent="end"><s-text accessibilityVisibility="exclusive">Actions</s-text><SortModeButton sorting={sorting} disabled={disabled || ids.length < 2} label="disclaimers" onChange={setSorting} /></s-stack></s-table-header></s-table-header-row>
        <s-table-body>{ids.map((id, index) => <s-table-row key={index}>
          <s-table-cell><div style={{display:'flex', alignItems:'center', gap:'8px', minWidth:0}}>{sorting && <SortControls index={index} count={ids.length} label={disclaimers[id]?.name || 'disclaimer'} disabled={disabled} onMove={move} />}<div style={{minWidth:0, overflowWrap:'anywhere'}}><s-text>{disclaimers[id]?.name || 'Disclaimer unavailable'}</s-text></div></div></s-table-cell>
          <s-table-cell><s-stack direction="inline" gap="small-100" justifyContent="end">
            {!sorting && <s-button variant="tertiary" icon="edit" accessibilityLabel={`Edit ${disclaimers[id]?.name || 'disclaimer'}`} disabled={disabled || !disclaimers[id]} onClick={() => openEditor(disclaimers[id])} />}
          </s-stack></s-table-cell>
        </s-table-row>)}{!ids.length && <s-table-row><s-table-cell><s-text color="subdued">No disclaimers yet.</s-text></s-table-cell><s-table-cell /></s-table-row>}</s-table-body>
      </s-table>}
      {!sorting && <s-stack direction="inline" gap="small">
        <s-button disabled={disabled || loading || ids.length >= MAX_LEGACY_DISCLAIMERS} onClick={() => openEditor()}>New disclaimer</s-button>
        <s-button disabled={disabled || loading || ids.length >= MAX_LEGACY_DISCLAIMERS} onClick={openAdd}>Select existing</s-button>
      </s-stack>}
    </s-stack>
    <s-modal ref={addModal} id="add-disclaimer-modal" heading="Select existing disclaimer">
      <s-stack gap="base">
        {dialogError && <s-banner tone="critical">{dialogError}</s-banner>}
        {catalogLoading ? <s-spinner accessibilityLabel="Loading available disclaimers" /> : catalog.length ? <s-select label="Existing disclaimer" value={selectedDisclaimerId} onChange={(event) => setSelectedDisclaimerId(event.currentTarget.value)}>
          {catalog.map((disclaimer) => <s-option key={disclaimer.id} value={disclaimer.id}>{disclaimer.name}</s-option>)}
        </s-select> : <s-text color="subdued">No other disclaimers are available.</s-text>}
      </s-stack>
      <s-button slot="secondary-actions" commandFor="add-disclaimer-modal" command="--hide">Cancel</s-button>
      <s-button slot="primary-action" variant="primary" disabled={catalogLoading || !selectedDisclaimerId} onClick={addExisting}>Select disclaimer</s-button>
    </s-modal>
    <s-modal ref={modal} id="disclaimer-editor" heading={editing?.original ? 'Edit disclaimer' : 'Add disclaimer'}>
      <s-stack gap="base">
        {dialogError && <s-banner tone="critical">{dialogError}</s-banner>}
        <s-text-field label="Internal name" required maxLength={255} value={editing?.name || ''} disabled={saving || Boolean(editing?.original)} onInput={(event) => setEditing((current) => ({...current, name: event.currentTarget.value}))} />
        <s-text-area label="Disclaimer" required rows={6} maxLength={50000} value={editing?.text || ''} disabled={saving || Boolean(editing?.original)} onInput={(event) => setEditing((current) => ({...current, text: event.currentTarget.value}))} />
        {adminHref
          ? <s-paragraph>Disclaimer is read-only here. <s-link href={adminHref} target="_blank">Edit the disclaimer in Shopify admin</s-link> to use the full rich-text editor.</s-paragraph>
          : <s-paragraph color="subdued">Enter the disclaimer as plain text. After creating the disclaimer, use Shopify admin for further disclaimer edits and rich-text formatting.</s-paragraph>}
      </s-stack>
      {editing?.original ? <>
        <s-button slot="secondary-actions" variant="tertiary" icon="delete" accessibilityLabel={`Remove ${editing.name || 'disclaimer'} from product`} commandFor="remove-disclaimer-modal" command="--show" disabled={saving} />
        <s-button slot="primary-action" variant="primary" commandFor="disclaimer-editor" command="--hide">Done</s-button>
      </> : <>
        <s-button slot="secondary-actions" commandFor="disclaimer-editor" command="--hide" disabled={saving}>Cancel</s-button>
        <s-button slot="primary-action" variant="primary" loading={saving} disabled={saving || !editing?.name.trim() || !editing?.text.trim()} onClick={save}>Save</s-button>
      </>}
    </s-modal>
    <s-modal id="terms-settings-modal" heading="Terms & restrictions settings">
      <s-choice-list label="Acceptance" name="terms-acceptance" values={[required ? 'required' : 'optional']} disabled={disabled} onChange={(event) => onRequiredChange(event.currentTarget.values[0] === 'required')}>
        <s-choice value="required">Required</s-choice>
        <s-choice value="optional">Not required</s-choice>
      </s-choice-list>
      <s-button slot="primary-action" variant="primary" commandFor="terms-settings-modal" command="--hide">Done</s-button>
    </s-modal>
    <s-modal id="remove-disclaimer-modal" heading="Remove disclaimer"><s-paragraph>Are you sure you want to remove this disclaimer from the product?</s-paragraph><s-button slot="secondary-actions" commandFor="remove-disclaimer-modal" command="--hide">Cancel</s-button><s-button slot="primary-action" variant="primary" tone="critical" commandFor="remove-disclaimer-modal" command="--hide" onClick={() => { if (editing?.original?.id) onChange(ids.filter((id) => id !== editing.original.handle)); modal.current?.hideOverlay(); }}>Remove disclaimer</s-button></s-modal>
  </s-section>;
}
